import { Entity } from "../../engine/entity/Entity";

import { Breed } from "../../engine/entity/Breed";
import { ActionController } from "./controllers/genericController";
import { Attack, Item } from "../items/Item";
import { SparseMap } from "../../lib/sparseMap";
import { Particle } from "../../engine/anims/Particle";

export enum Encumbrance {
  LIGHT,
  MEDIUM,
  HEAVY,
}
export class Stat {
  emptyTriggered = false;
  current = 0;
  constructor(
    public max: number,
    private trigger?: null | (() => void),
    startFull?: boolean
  ) {
    if (startFull) {
      this.current = max;
    }
  }

  change(value: number) {
    if (0 < value) {
      this.emptyTriggered = false;
      this.current = Math.min(this.max, this.current + value);
    } else {
      this.current = Math.max(0, this.current + value);
    }
    if (!this.current && this.trigger && !this.emptyTriggered) {
      this.emptyTriggered = true;
      this.trigger();
    }
  }

  adjustMax(value: number, fill?: boolean) {
    this.max = Math.max(0, this.max + value);
    if (!fill) {
      this.current = Math.min(this.max, this.current);
    } else {
      this.emptyTriggered = false;
      this.change(value);
    }
  }

  fill() {
    this.emptyTriggered = false;
    this.current = this.max;
  }
}

const EMPTY_BASE_STATS = {
  level: 0,
  health: 0,
  stamina: 0,
  poise: 0,
  armor: 0,
  weight: 0,
  strength: 0,
  force: 0,
};

export enum WieldMode {
  oneHanded,
  twoHanded,
}

const defaultAttack: Attack = {
  damage: 1,
  poise: 5,
  stamina: 20,
};

export class ActorBreed extends Breed {
  baseStats = EMPTY_BASE_STATS;
  statTriggers = {};
  defaultWielding: { left?: string; right?: string; mode?: WieldMode } = {};
  defaultWearing: { armor?: string } = {};
  defaultContains: [string, number][] = [];
  baseAttack: Attack;
  controller: ActionController | null = null;

  constructor(key: string, breedData: any) {
    super(key, breedData);
    this.controller = breedData.actionController;
    this.baseStats = breedData.baseStats || EMPTY_BASE_STATS;
    this.statTriggers = breedData.triggers || {};
    this.defaultWielding = breedData.wielding;
    this.defaultWearing = breedData.wearing;
    this.defaultContains = breedData.contains;
    this.baseAttack = breedData.baseAttack || defaultAttack;
  }
}

export enum Hands {
  LEFT,
  RIGHT,
}

export class Actor extends Entity<ActorBreed> {
  alive = true;
  level: number;
  health: Stat;
  stamina: Stat;
  poise: Stat;
  wielding: {
    left?: Item | null;
    right?: Item | null;
    mode: WieldMode;
    activeHand: Hands;
  } = { mode: WieldMode.oneHanded, activeHand: Hands.LEFT };
  wearing: { armor?: Item | null } = {};
  contains: SparseMap<Item> = new SparseMap();
  attributes: {
    force: number;
    strength: number;
    weight: number;
    armor: number;
  };
  status: {
    waited?: boolean;
    moved?: boolean;
    hit?: boolean;
    staggered?: boolean;
    staggeredCount?: number;
    blocking?: boolean;
    replanned?: boolean;
    sprinted?: boolean;
  } = {};
  ownedParticles: Map<number, Particle<any>> = new Map();

  controllerProps: any;
  seenPlayer = 0; //Turns since last seen player

  constructor(id: number, breed: ActorBreed, x: number, y: number) {
    super(id, breed, x, y);
    this.level = breed.baseStats.level;
    this.health = new Stat(
      breed.baseStats.health,
      breed.statTriggers.health && breed.statTriggers.health.bind(null, this),
      true
    );
    this.stamina = new Stat(
      breed.baseStats.stamina,
      breed.statTriggers.stamina && breed.statTriggers.stamina.bind(null, this),
      true
    );
    this.poise = new Stat(
      breed.baseStats.poise,
      breed.statTriggers.poise && breed.statTriggers.poise.bind(null, this),
      true
    );

    this.attributes = {
      force: breed.baseStats.force,
      strength: breed.baseStats.strength,
      weight: breed.baseStats.weight,
      armor: breed.baseStats.armor,
    };
  }
  plan() {
    return this.breed.controller && this.breed.controller(this);
  }

  end() {
    this.alive = false;
    this.ownedParticles.forEach((p) => p.finish());
  }

  async addParticle(p: Particle<any>) {
    this.ownedParticles.set(p.id, p);
    await p.status;
    this.ownedParticles.delete(p.id);
  }
}
